interface PathTreeNode {
  name: string;
  path: string;
  type: "tree" | "blob";
  children: Map<string, PathTreeNode>;
  depth: number;
}

class SimulationPathTreeBuilder {
  /**
   * Builds a path tree from simulation steps that have source code mappings
   */
  static buildPathTree(simSteps: SimStep[]): PathTreeNode {
    const root: PathTreeNode = {
      name: "",
      path: "",
      type: "tree",
      children: new Map(),
      depth: 0,
    };

    // Extract unique file paths from simsteps (including both nodes and edges)
    const filePaths = new Set<string>();
    simSteps.forEach((step) => {
      if (step.sourceCodeMapping?.filePath) {
        filePaths.add(step.sourceCodeMapping.filePath);
      }
    });

    // Build tree from paths
    filePaths.forEach((filePath) => {
      const segments = filePath.split("/").filter(Boolean);
      let currentNode = root;
      let currentPath = "";

      segments.forEach((segment, idx) => {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        const isLeaf = idx === segments.length - 1;

        if (!currentNode.children.has(segment)) {
          const newNode: PathTreeNode = {
            name: segment,
            path: currentPath,
            type: isLeaf ? "blob" : "tree",
            children: new Map(),
            depth: idx + 1,
          };
          currentNode.children.set(segment, newNode);
        }

        currentNode = currentNode.children.get(segment)!;
      });
    });

    return root;
  }

  /**
   * Get nodes by depth level for breadth-first traversal
   */
  static getNodesByDepth(root: PathTreeNode): PathTreeNode[][] {
    const levels: PathTreeNode[][] = [];

    const traverse = (node: PathTreeNode, depth: number) => {
      if (!levels[depth]) {
        levels[depth] = [];
      }

      if (node.path) {
        // Skip root node
        levels[depth].push(node);
      }

      node.children.forEach((child) => {
        traverse(child, depth + 1);
      });
    };

    traverse(root, 0);
    return levels;
  }
}

// Explicit exports
export { SimulationPathTreeBuilder };
export type { PathTreeNode };
